/**
 * Synthesize the in-ability file tree for a bundled skill.
 *
 * The `ability_files` table was dropped in the consolidation migration
 * (20260529100000) and `GET /api/abilities/:id/files` is now retired (410 Gone).
 * A skill's real content lives on the canonical `skills` row:
 * `content` is the SKILL.md body and `files` are its reference files. This
 * helper maps that canonical item into the ability-relative `AbilityFile[]`
 * shape the drill-in view renders (everything under `skills/<slug>/`).
 */

import type { AbilityFile } from "@bash/shared";

const PRIMARY_FILE = "SKILL.md";

export function buildSkillFileList(
  abilityId: string,
  skillSlug: string,
  item: {
    content?: string | null;
    files?: Array<{ path: string; content: string }> | null;
  }
): AbilityFile[] {
  const prefix = `skills/${skillSlug}/`;

  const make = (relPath: string, content: string): AbilityFile => ({
    id: `${abilityId}:${prefix}${relPath}`,
    ability_id: abilityId,
    path: `${prefix}${relPath}`,
    filename: relPath.split("/").pop() ?? relPath,
    content,
    storage_path: null,
    content_type: null,
    size_bytes: content.length,
    is_binary: false,
    checksum: null,
    created_at: "",
    updated_at: "",
  });

  // SKILL.md (the primary content field) always leads.
  const files: AbilityFile[] = [make(PRIMARY_FILE, item.content ?? "")];

  for (const f of item.files ?? []) {
    const rel = f.path.replace(/^\/+/, "");
    // SKILL.md is the primary content field, never a bundled reference file.
    if (!rel || rel === PRIMARY_FILE) continue;
    files.push(make(rel, f.content ?? ""));
  }

  return files;
}

/**
 * Summarize a skill's bundle for the My Kit affordance ("Bundled app · N files").
 * Reads only the already-fetched row (files + storage_assets), no new fetch.
 */
export function summarizeSkillBundle(item: {
  files?: unknown[];
  storage_assets?: Array<{ kind?: string }> | null;
}): { isBundled: boolean; fileCount: number } {
  const isBundled = (item.storage_assets ?? []).some(
    (a) => a?.kind === "archive"
  );
  const fileCount = Array.isArray(item.files) ? item.files.length : 0;
  return { isBundled, fileCount };
}
